import { supabaseClient, useUser } from '@auth-v2';
import { useMutation, useQuery } from '@tanstack/react-query';

import { addUserClub, Club, fetchAllClubs, fetchGolfClubs, fetchUserClubs, removeUserClub } from '@data';
import { currentUserIdAtom, useEnsureAtomValue } from '@store';

import { queryClient } from '../config/client';

const getClubsKey = () => ['clubs'] as const;

export const useClubCollections = () => {
    return useQuery({
        queryKey: getClubsKey(),
        queryFn: () => fetchGolfClubs(supabaseClient),
    });
};

export const useClubs = () => {
    return useQuery({
        queryKey: getClubsKey(),
        queryFn: () => fetchAllClubs(supabaseClient),
    });
};

const getUserClubsKey = (userId = 'current') => [userId, 'clubs', 'user'] as const;

export const useUserClubs = (userId?: string) => {
    const { data: user } = useUser();
    return useQuery({
        queryKey: getUserClubsKey(userId),
        queryFn: () => fetchUserClubs(supabaseClient, userId ?? user!.id),
        enabled: !!user?.id,
    });
};

export const useSelectUserClub = (userId?: string) => {
    const currentUserId = useEnsureAtomValue(currentUserIdAtom);
    return useMutation({
        mutationFn: async (clubId: number) => {
            const userClubs = queryClient.getQueryData(getUserClubsKey(userId ?? currentUserId)) as Club[];
            const club = userClubs?.find((club) => club.id === clubId);
            const selectAction = club ? removeUserClub : addUserClub;
            await selectAction(supabaseClient, userId ?? currentUserId, clubId);
        },
        onSuccess: () =>
            queryClient.invalidateQueries({
                queryKey: getUserClubsKey(userId ?? currentUserId),
            }),
    });
};
